import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import {
  type AccountOthersData,
  GET_ACCOUNT_OTHERS
} from '../queries/accountOthersQuery';

interface FieldMapping {
  apiField: string;
  formField: keyof AccountOthersData;
  isDate?: boolean;
}

const INVESTMENT_SPONSOR_FIELDS: FieldMapping[] = [
  { apiField: 'bdc', formField: 'bdc' },
  { apiField: 'esgFocused', formField: 'esgFocused' },
  { apiField: 'isIcapitalPlatform', formField: 'isIcapitalPlatform' },
  {
    apiField: 'nonSponsorTransactions',
    formField: 'nonSponsorTransactions'
  },
  { apiField: 'sbicQualifying', formField: 'sbicQualifying' },
  { apiField: 'sponsorTransactions', formField: 'sponsorTransactions' },
  { apiField: 'sponsorInformation', formField: 'sponsorInformation' },
  { apiField: 'preqinFirmId', formField: 'preqinFirmId' },
  { apiField: 'sqlDate', formField: 'sqlDate', isDate: true },
  { apiField: 'typeOfInvestor', formField: 'typeOfInvestor' },
  { apiField: 'revolverNonAbl', formField: 'revolverNonAbl' }
];

function getChangedValues(
  fields: FieldMapping[],
  current: AccountOthersData,
  initial: AccountOthersData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const { apiField, formField, isDate } of fields) {
    const currentVal = current[formField];
    const initialVal = initial[formField];

    const hasChanged = isDate
      ? toDateInputValue(currentVal as string) !==
        toDateInputValue(initialVal as string)
      : currentVal !== initialVal;

    if (hasChanged) {
      if (isDate) {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      } else if (typeof currentVal === 'boolean') {
        changes[apiField] = currentVal;
      } else {
        changes[apiField] = currentVal === '' ? null : (currentVal as string);
      }
    }
  }

  return changes;
}

function toDateInputValue(isoDate: string): string {
  if (!isoDate) return '';
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? isoDate : date.toISOString().split('T')[0];
}

const UPDATE_ACCOUNT = gql`
  mutation UpdateAccount($id: ID!, $values: AccountInput!) {
    updateAccount(id: $id, values: $values) {
      ok
      account {
        id
        bdc
        esgFocused
        isIcapitalPlatform
        nonSponsorTransactions
        sbicQualifying
        sponsorTransactions
        sponsorInformation
        preqinFirmId
        sqlDate
        typeOfInvestor
        revolverNonAbl
      }
    }
  }
`;

export interface EditInvestmentSponsorInfoDialogProps {
  accountId: string;
  initialValue: AccountOthersData;
  isOpen: boolean;
  onChange: (data: AccountOthersData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: AccountOthersData;
}

export function EditInvestmentSponsorInfoDialog({
  accountId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditInvestmentSponsorInfoDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateAccount] = useMutation(UPDATE_ACCOUNT, {
    refetchQueries: [
      { query: GET_ACCOUNT_OTHERS, variables: { id: accountId } }
    ]
  });

  const changes = getChangedValues(
    INVESTMENT_SPONSOR_FIELDS,
    value,
    initialValue
  );
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof AccountOthersData>(
    field: K,
    fieldValue: AccountOthersData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateAccount({
        variables: { id: accountId, values: changes }
      });

      const data = response.data as {
        updateAccount?: { ok?: boolean };
      };

      if (data.updateAccount?.ok === false) {
        toast.error('Failed to save investment & sponsor info');
      } else {
        toast.success('Investment & sponsor info saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving investment & sponsor info:', error);
      toast.error('Failed to save investment & sponsor info');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Investment & Sponsor Info"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditCheckboxField
          checked={value.bdc}
          label="BDC"
          onCheckedChange={bdc => {
            updateField('bdc', bdc);
          }}
        />
        <EditCheckboxField
          checked={value.esgFocused}
          label="ESG-focused"
          onCheckedChange={esgFocused => {
            updateField('esgFocused', esgFocused);
          }}
        />
        <EditCheckboxField
          checked={value.isIcapitalPlatform}
          label="Is iCapital Platform"
          onCheckedChange={isIcapitalPlatform => {
            updateField('isIcapitalPlatform', isIcapitalPlatform);
          }}
        />
        <EditCheckboxField
          checked={value.nonSponsorTransactions}
          label="Non-Sponsor Transactions"
          onCheckedChange={nonSponsorTransactions => {
            updateField('nonSponsorTransactions', nonSponsorTransactions);
          }}
        />
        <EditCheckboxField
          checked={value.sbicQualifying}
          label="SBIC Qualifying"
          onCheckedChange={sbicQualifying => {
            updateField('sbicQualifying', sbicQualifying);
          }}
        />
        <EditCheckboxField
          checked={value.sponsorTransactions}
          label="Sponsor Transactions"
          onCheckedChange={sponsorTransactions => {
            updateField('sponsorTransactions', sponsorTransactions);
          }}
        />
        <EditFormField
          label="Sponsor Information"
          onChange={sponsorInformation => {
            updateField('sponsorInformation', sponsorInformation);
          }}
          value={value.sponsorInformation}
        />
        <EditFormField
          label="Preqin Firm ID"
          onChange={preqinFirmId => {
            updateField('preqinFirmId', preqinFirmId);
          }}
          value={value.preqinFirmId}
        />
        <EditFormField
          label="SQL Date"
          onChange={sqlDate => {
            updateField('sqlDate', sqlDate);
          }}
          type="date"
          value={toDateInputValue(value.sqlDate)}
        />
        <EditDynamicSelectField
          fieldName="type_of_investor"
          label="Type of Investor"
          model="opstream.account"
          onChange={v => {
            updateField('typeOfInvestor', v);
          }}
          value={value.typeOfInvestor}
        />
        <EditCheckboxField
          checked={value.revolverNonAbl}
          label="Revolver Non ABL"
          onCheckedChange={revolverNonAbl => {
            updateField('revolverNonAbl', revolverNonAbl);
          }}
        />
      </div>
    </EditDialog>
  );
}
